import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Sector, Tooltip, ResponsiveContainer, Label } from 'recharts';

import { PieChartPropTypes } from 'src/interfaces/iproptypes';
import { COLOR_GREY_600 } from 'src/utils/colors';
import { DataChartTooltip } from 'src/App/Dashboards/Market/Components/Tooltips';
import { formatCurrency } from 'src/utils/functions';
import { Box } from '@mui/material';
import { logAmplitudeEvent } from 'src/utils/analytics';

const RADIAN = Math.PI / 180;

const DynamicPieChart = ({
  data,
  unit,
  pieColors,
  label,
  pieAccentColor,
  radius,
  tooltipTextSuffix,
  getTooltipText,
  parent,
}: PieChartPropTypes) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const innerRadius = radius ? radius : 50;
  const outerRadius = innerRadius + 20;

  const renderActiveShape = props => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={pieAccentColor}
        />
      </g>
    );
  };

  const renderPiechartLabelOuter = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    if (percent > 0.02) {
      return (
        <text
          x={x}
          y={y}
          fill="white"
          textAnchor="middle"
          style={{ fontSize: '0.75rem', fontFamily: 'Roboto' }}
          dominantBaseline="central"
        >
          {`${data[index].legend}`}
        </text>
      );
    }
    return;
  };

  const CustomTooltip = ({ active, payload, label, classes }) => {
    useEffect(() => {
      if (active && parent) {
        logAmplitudeEvent('tooltip_interacted', {
          tooltip_type: 'graph',
          parent: parent,
        });
      }
    }, [active]);

    if (active && payload) {
      const tooltipData = [
        {
          dataKey: payload[0].name,
          val: getTooltipText
            ? getTooltipText(payload[0].value)
            : `${formatCurrency(payload[0].value.toString())} ${unit} ${
                unit !== '%' ? '( ' + payload[0].payload.percentage + '% )' : ''
              }` + (tooltipTextSuffix ? ' ' + tooltipTextSuffix : ''),
          color: pieAccentColor,
        },
      ];
      return <DataChartTooltip data={tooltipData} />;
    }
    return null;
  };

  return (
    <ResponsiveContainer width={'100%'} height={180}>
      <PieChart margin={{ top: 0, left: 0, right: 0, bottom: 0 }}>
        <Pie
          activeIndex={activeIndex}
          activeShape={renderActiveShape}
          data={data}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          isAnimationActive={false}
          labelLine={false}
          label={renderPiechartLabelOuter}
          dataKey="value"
          onMouseEnter={(_, index) => setActiveIndex(index)}
        >
          {data.map((entry, index) => {
            return <Cell key={`cell-${index}`} fill={pieColors[index]} />;
          })}

          {label && (
            <Label
              value={label}
              offset={0}
              position="center"
              style={{
                fontSize: '0.75rem',
                fontFamily: 'Roboto',
              }}
              fill={COLOR_GREY_600}
            />
          )}
        </Pie>
        <Tooltip content={<CustomTooltip classes active payload label />} position={{ y: -50 }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default DynamicPieChart;
